import { supabase } from '@/lib/supabase'

/* RLS does the authorization, and here it does something the other feature
   folders never rely on: `picks_select_pool_after_lock` (0002) grants every pool
   member SELECT on every member's pick row once game_week_is_locked() is true.

   So this needs no migration and no RPC -- unlike the commissioner roster, whose
   whole problem was wanting the same data BEFORE lock. The deliberate absence of
   a user_id filter is what makes this the pool's board rather than one member's
   picks; see the note in features/picks/api.ts, which filters for exactly the
   opposite reason.

   Before lock this returns an empty array rather than an error, which is
   correct: nobody's picks are public yet. The page renders that as a waiting
   state instead of an empty grid. */
export async function fetchWeekPicks(poolId: string, weekId: string) {
  // picks has no week_id; games!inner(week_id) is both the join and the filter.
  const { data, error } = await supabase
    .from('picks')
    .select('user_id, game_id, picked_team, is_lock, games!inner(week_id)')
    .eq('pool_id', poolId)
    .eq('games.week_id', weekId)
  if (error) throw error
  return data
}

/* Column headings for the grid, and the "no pick" roll-up on the cards.

   Sourced from pool_members + profiles rather than season_standings, which also
   carries display_name. season_standings is built on weekly_results, which drops
   any member whose joined_at is later than a week's picks_lock_at and any week
   before the pool's start_week -- so a member who joined mid-season is missing
   from it until they have played a week. On a board whose job is showing who
   picked and who did not, a member silently absent from the columns is the one
   failure that matters. Membership is the right source; scoring history is not.

   profiles is fetched separately rather than embedded because the only FK path
   is pool_members_user_id_fkey with isOneToOne: false, so an embed types as an
   array. Both reads are covered by RLS: pool_members_select_member and
   profiles_select_pool_comembers. */
export async function fetchPoolRoster(poolId: string) {
  const { data: members, error } = await supabase
    .from('pool_members')
    .select('user_id')
    .eq('pool_id', poolId)
  if (error) throw error

  const ids = members.map((member) => member.user_id)
  if (ids.length === 0) return []

  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('id, display_name')
    .in('id', ids)
  if (profilesError) throw profilesError

  // Sorted here so the grid's column order is stable across refetches; neither
  // table has an ordering that means anything to a reader.
  return profiles
    .map((profile) => ({ userId: profile.id, name: profile.display_name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}
